import './ProjectListImportSelector.scss';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'src/modules/common/components/Button';
import { CustomLoading } from 'src/modules/common/components/CustomLoading';
import { ModalBody } from 'src/modules/common/components/ModalBody';
import { ModalFooter } from 'src/modules/common/components/ModalFooter';
import { CircleCheckFilledIcon } from 'src/modules/common/icons/CircleCheckFilledIcon';
import { Future } from 'src/modules/common/types/Future';
import { toNullable } from 'src/modules/common/utils/either';
import { ProjectListImportData, ProjectListImportEntry } from 'src/modules/project-list/types/ProjectListImport';
import { ProjectListImportDataTable } from 'src/modules/project-list/views/ProjectListImportDataTable';
import { isNotNull } from 'src/utils/guard';

type Props = {
  readonly content: ProjectListImportData;
  readonly status: Future<void> | null;
  readonly onSubmit: (entries: ReadonlyArray<ProjectListImportEntry>) => void;
  readonly onCancel: () => void;
  readonly onComplete: () => void;
};

export const ProjectListImportSelector = ({
  content,
  status,
  onSubmit,
  onCancel,
  onComplete,
}: Props): React.ReactElement => {
  const [selected, setSelected] = useState<ReadonlyArray<ProjectListImportEntry>>(() => (
    content
      .map((it) => it.importData)
      .map(toNullable)
      .filter(isNotNull)
  ));

  const handleSubmit = useCallback(() => onSubmit(selected), [selected, onSubmit]);

  return (
    <>
      <ModalBody shaded={true}>
        <div className="bp-project-list-import-selector">
          {status?.state === 'pending' && (
            <div className="bp-project-list-import-selector__result-wrapper">
              <CustomLoading/>

              <p className="bp-project-list-import-selector__result-text">
                <FormattedMessage id="projectList/importModal/selector/loading"/>
              </p>
            </div>
          )}

          {status?.state === 'success' && (
            <div className="bp-project-list-import-selector__result-wrapper">
              <div className="bp-project-list-import-selector__result-wrapper-icon">
                <CircleCheckFilledIcon/>
              </div>

              <p className="bp-project-list-import-selector__result-text">
                <FormattedMessage id="projectList/importModal/selector/success"/>
              </p>
            </div>
          )}

          {(status === null || status.state === 'failure') && (
            <div className="bp-project-list-import-selector__result-wrapper">
              <ProjectListImportDataTable
                content={content}
                selected={selected}
                onChange={setSelected}
              />
            </div>
          )}
        </div>
      </ModalBody>

      <ModalFooter narrowFooter={true}>
        {status?.state !== 'success' && (
          <>
            <Button
              kind="secondary"
              autoFocus={true}
              onClick={onCancel}
              fullWidth={true}
              disabled={status?.state === 'pending'}
            >
              <FormattedMessage id="projectList/importModal/selector/cancel"/>
            </Button>

            <Button
              kind="primary"
              onClick={handleSubmit}
              fullWidth={true}
              disabled={selected.length === 0 || status?.state === 'pending'}
              loading={status?.state === 'pending'}
            >
              <FormattedMessage id="projectList/importModal/selector/submit"/>
            </Button>
          </>
        )}

        {status?.state === 'success' && (
          <Button
            className="bp-project-list-import-modal__button"
            onClick={onComplete}
            fullWidth={true}
          >
            <FormattedMessage id="projectList/importModal/selector/close"/>
          </Button>
        )}
      </ModalFooter>
    </>
  );
};
